import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useHubToast } from "@/components/ui/HubToast";

type OrgNode = {
  staffId: number;
  fullName: string;
  jobTitle: string | null;
  active: boolean;
  roleNames: string[];
  reports: OrgNode[];
};

type DirectoryRow = { staffId: number; fullName: string; active: boolean };

function OrgBranch({ node }: { node: OrgNode }) {
  return (
    <li className="mt-2">
      <div className="text-sm">
        <Link className="font-medium text-brand-700 hover:underline" to={`/people/staff/${node.staffId}`}>
          {node.fullName}
        </Link>
        {node.active ? "" : " (inactive)"}
        {node.jobTitle ? <span className="text-slate-600"> · {node.jobTitle}</span> : null}
        {node.roleNames.length > 0 ? (
          <span className="text-slate-500"> · {node.roleNames.join(", ")}</span>
        ) : null}
      </div>
      {node.reports.length > 0 ? (
        <ul className="ml-5 border-l border-slate-200 pl-3">
          {node.reports.map((child) => (
            <OrgBranch key={child.staffId} node={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function PeopleOrgChartPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("people");
  const readOnlyHint = "Your role has read-only access to the People module.";

  const chartQuery = useQuery({
    queryKey: ["people", "org"],
    queryFn: () => apiFetch<{ roots: OrgNode[]; cycleStaffIds: number[] }>("/api/people/org"),
  });
  const directoryQuery = useQuery({
    queryKey: ["people", "directory"],
    queryFn: () => apiFetch<{ rows: DirectoryRow[] }>("/api/people/directory"),
  });
  const reviewersQuery = useQuery({
    queryKey: ["people", "disciplinary-reviewers"],
    queryFn: () => apiFetch<{ canManage: boolean; staffIds: number[] }>("/api/people/disciplinary-reviewers"),
  });

  const saveReviewers = useMutation({
    mutationFn: (staffIds: number[]) =>
      apiFetch("/api/people/disciplinary-reviewers", {
        method: "PUT",
        body: { staffIds },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["people", "disciplinary-reviewers"] });
      showToast({ message: "Disciplinary reviewers saved.", tone: "success" });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const roots = chartQuery.data?.roots ?? [];
  const cycleIds = chartQuery.data?.cycleStaffIds ?? [];
  const people = directoryQuery.data?.rows ?? [];
  const reviewerIds = new Set(reviewersQuery.data?.staffIds ?? []);

  function toggleReviewer(staffId: number) {
    const next = new Set(reviewerIds);
    if (next.has(staffId)) next.delete(staffId);
    else next.add(staffId);
    saveReviewers.mutate([...next]);
  }

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Each person is nested under the Manager saved on their record. Hub role names come from Admin, Hub accounts. A reporting loop is listed at the top instead of drawing an endless tree.">
        Org chart
      </PageTitleWithHelp>

      {chartQuery.isError ? (
        <p className="text-sm text-rose-700" role="alert">
          {(chartQuery.error as Error).message}
        </p>
      ) : null}

      {cycleIds.length > 0 ? (
        <p className="text-sm text-amber-800" role="status">
          The reporting line loops for {cycleIds.length} people. They are shown at the top until a manager is corrected.
        </p>
      ) : null}

      <ul>
        {roots.map((node) => (
          <OrgBranch key={node.staffId} node={node} />
        ))}
      </ul>

      {reviewersQuery.data?.canManage ? (
        <HubSettingsSection
          title="Disciplinary reviewers"
          hoverHint="These people can read every counseling and discipline note. Everyone else sees a note only for people who report to them. Keep yourself on this list."
        >
          <ul className="space-y-2">
            {people
              .filter((person) => person.active || reviewerIds.has(person.staffId))
              .map((person) => (
                <li key={person.staffId}>
                  <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={reviewerIds.has(person.staffId)}
                        disabled={!writesAllowed || saveReviewers.isPending}
                        onChange={() => toggleReviewer(person.staffId)}
                      />
                      {person.fullName}
                      {person.active ? "" : " (inactive)"}
                    </label>
                  </ReadOnlyHoverLayer>
                </li>
              ))}
          </ul>
        </HubSettingsSection>
      ) : null}
    </div>
  );
}
